import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
  cancelMembership,
  getUserSubscription,
  reSubscribeUser,
  subscribeUser,
  updatePaymentMethod,
} from "../actions/subscriptionAction";
import { ISubscription } from "../../utility/types/subscriptionType";

export interface SubscriptionState {
  subscriptionData: ISubscription | null;
  isLoading: boolean;
  error: string;
}

interface SubscriptionStatusPayload {
  subscriptionId: string;
  status: string;
}

const initialState: SubscriptionState = {
  subscriptionData: null,
  isLoading: false,
  error: "",
};

const subscriptionSlice = createSlice({
  name: "subscription",
  initialState,
  reducers: {
    updateSubscriptionStatus: (
      state,
      action: PayloadAction<SubscriptionStatusPayload>
    ) => {
      if (state.subscriptionData) {
        state.subscriptionData.subscriptionStatus = action.payload.status;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(subscribeUser.pending, (state, action) => {
      state.isLoading = true;
    });

    builder.addCase(subscribeUser.fulfilled, (state, action) => {
      state.isLoading = false;
      state.subscriptionData = action.payload;
    });

    builder.addCase(subscribeUser.rejected, (state, action) => {
      state.isLoading = false;
      state.error =
        typeof action.payload == "string"
          ? action.payload
          : "Error. Failed to subscribe user";
    });

    builder.addCase(reSubscribeUser.pending, (state, action) => {
      state.isLoading = true;
    });

    builder.addCase(reSubscribeUser.fulfilled, (state, action) => {
      state.isLoading = false;
      state.subscriptionData = action.payload;
    });

    builder.addCase(reSubscribeUser.rejected, (state, action) => {
      state.isLoading = false;
      state.error =
        typeof action.payload == "string"
          ? action.payload
          : "Error. Failed to re-subscribe user";
    });

    builder.addCase(getUserSubscription.pending, (state, action) => {
      state.isLoading = true;
    });

    builder.addCase(getUserSubscription.fulfilled, (state, action) => {
      state.isLoading = false;
      state.subscriptionData = action.payload;
    });

    builder.addCase(getUserSubscription.rejected, (state, action) => {
      state.isLoading = false;

      if (
        action.payload === "No subscription found for this user." ||
        action.error.message === "Rejected"
      ) {
        state.subscriptionData = null;
      }
      state.error =
        typeof action.payload === "string"
          ? action.payload || "ERROR: Failed to get user subscription."
          : "ERROR: Failed to get user subscription.";
    });

    builder.addCase(updatePaymentMethod.pending, (state, action) => {
      state.isLoading = true;
    });

    builder.addCase(updatePaymentMethod.fulfilled, (state, action) => {
      state.isLoading = false;
      state.subscriptionData = action.payload;
    });

    builder.addCase(updatePaymentMethod.rejected, (state, action) => {
      state.isLoading = false;
      state.error =
        typeof action.payload === "string"
          ? action.payload
          : "ERROR: Failed to update payment method.";
    });

    builder.addCase(cancelMembership.pending, (state, action) => {
      state.isLoading = true;
    });

    builder.addCase(cancelMembership.fulfilled, (state, action) => {
      state.isLoading = false;
      state.subscriptionData = action.payload;
    });

    builder.addCase(cancelMembership.rejected, (state, action) => {
      state.isLoading = false;
      state.error =
        typeof action.payload === "string"
          ? action.payload
          : "ERROR: Failed to cancel membership subscription.";
    });
  },
});

export const { updateSubscriptionStatus } = subscriptionSlice.actions;

export default subscriptionSlice.reducer;
